import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000').replace(/\/$/, '');

type TranscriptSpeaker = 'operator' | 'client';

interface ConnectDemoRealtimeOptions {
  scenarioId: string;
  sessionToken: string;
  onTranscript: (payload: { speaker: TranscriptSpeaker; text: string }) => void;
}

interface DemoRealtimeSessionResponse {
  value?: string;
  client_secret?: {
    value?: string;
    expires_at?: number;
  };
  sessionConfig?: {
    model?: string;
    voice?: string;
    scenarioId?: string;
    openingInstruction?: string;
    maxTurns?: number;
    demoMode?: boolean;
  };
}

function safeJsonParse(raw: string) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

async function safeReadJson(response: Response) {
  const text = await response.text();
  const parsed = safeJsonParse(text);
  return { text, parsed };
}

export function useDemoRealtime() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const transcriptBufferRef = useRef<Record<string, string>>({});
  const transcriptCallbackRef = useRef<ConnectDemoRealtimeOptions['onTranscript'] | null>(null);

  const cleanup = useCallback(() => {
    channelRef.current?.close();
    channelRef.current = null;

    peerRef.current?.close();
    peerRef.current = null;

    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current.remove();
      audioElementRef.current = null;
    }

    transcriptBufferRef.current = {};
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const sendEvent = useCallback((event: Record<string, unknown>) => {
    const channel = channelRef.current;
    if (!channel || channel.readyState !== 'open') {
      throw new Error('Canal Realtime indisponivel');
    }
    channel.send(JSON.stringify(event));
  }, []);

  const handleServerEvent = useCallback((event: any) => {
    if (!event?.type) return;

    if (event.type === 'conversation.item.input_audio_transcription.completed' && event.transcript) {
      transcriptCallbackRef.current?.({
        speaker: 'operator',
        text: String(event.transcript).trim(),
      });
      return;
    }

    if (event.type === 'response.output_audio_transcript.delta') {
      const key = String(event.item_id || event.response_id || 'assistant');
      transcriptBufferRef.current[key] = `${transcriptBufferRef.current[key] || ''}${event.delta || ''}`;
      return;
    }

    if (event.type === 'response.output_audio_transcript.done') {
      const key = String(event.item_id || event.response_id || 'assistant');
      const finalText = String(event.transcript || transcriptBufferRef.current[key] || '').trim();
      delete transcriptBufferRef.current[key];
      if (finalText) {
        transcriptCallbackRef.current?.({
          speaker: 'client',
          text: finalText,
        });
      }
      return;
    }

    if (event.type === 'error') {
      setError(event.error?.message || 'Erro na sessao Realtime');
    }
  }, []);

  const connect = useCallback(async ({ scenarioId, sessionToken, onTranscript }: ConnectDemoRealtimeOptions) => {
    setError(null);
    setIsConnecting(true);
    transcriptCallbackRef.current = onTranscript;

    try {
      cleanup();

      const sessionResponse = await fetch(`${API_BASE_URL}/api/demo-realtime/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-demo-session-token': sessionToken,
        },
        body: JSON.stringify({ scenarioId, sessionToken }),
      });

      const { text, parsed } = await safeReadJson(sessionResponse);
      const sessionJson = parsed || {};
      if (!sessionResponse.ok) {
        throw new Error(sessionJson.error || text || 'Falha ao criar sessao de voz');
      }

      const realtimeData = (sessionJson.data || {}) as DemoRealtimeSessionResponse;
      const ephemeralKey = realtimeData.client_secret?.value || realtimeData.value;
      if (!ephemeralKey) {
        throw new Error('Sessao Realtime sem client_secret retornado');
      }

      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = localStream;

      const peer = new RTCPeerConnection();
      peerRef.current = peer;

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElementRef.current = audioEl;

      peer.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];
      };

      peer.onconnectionstatechange = () => {
        const state = peer.connectionState;
        if (state === 'connected') {
          setIsConnected(true);
          setIsConnecting(false);
        }
        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          setIsConnected(false);
        }
      };

      localStream.getTracks().forEach((track) => peer.addTrack(track, localStream));

      const channel = peer.createDataChannel('oai-events');
      channelRef.current = channel;
      channel.addEventListener('message', (messageEvent) => {
        const parsed = safeJsonParse(messageEvent.data);
        if (parsed) handleServerEvent(parsed);
      });

      const channelOpenPromise = new Promise<void>((resolve, reject) => {
        channel.addEventListener('open', () => resolve(), { once: true });
        channel.addEventListener('error', () => reject(new Error('Falha no canal de dados Realtime')), { once: true });
      });

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);

      const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp || '',
      });

      if (!sdpResponse.ok) {
        const text = await sdpResponse.text();
        throw new Error(text || 'Falha ao negociar sessao Realtime');
      }

      const answerSdp = await sdpResponse.text();
      await peer.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

      await channelOpenPromise;
      setIsConnected(true);
      setIsConnecting(false);

      const openingInstruction = realtimeData.sessionConfig?.openingInstruction
        || 'Voce e o cliente. Cumprimente brevemente e diga o motivo do contato na primeira fala.';

      sendEvent({
        type: 'response.create',
        response: {
          instructions: openingInstruction,
          output_modalities: ['audio'],
        },
      });
    } catch (err: any) {
      cleanup();
      setError(err?.message || 'Erro ao conectar a sessao de voz');
      throw err;
    } finally {
      setIsConnecting(false);
    }
  }, [cleanup, handleServerEvent, sendEvent]);

  const disconnect = useCallback(() => {
    cleanup();
  }, [cleanup]);

  const sendTextMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: trimmed,
          },
        ],
      },
    });

    sendEvent({
      type: 'response.create',
      response: {
        output_modalities: ['audio'],
      },
    });
  }, [sendEvent]);

  useEffect(() => () => cleanup(), [cleanup]);

  return {
    connect,
    disconnect,
    sendTextMessage,
    isConnecting,
    isConnected,
    error,
  };
}
